import React, { useState, useRef } from 'react';
import {
  ChevronDown, ChevronUp, Trash2, GripVertical,
  MessageCircle, Send, Clock, Flag, Lock, GitBranch,
  ImagePlus, Loader2, X as XIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  Step, AskStep, SendTemplateStep, DelayStep, FinishStep, BranchStep,
  ResponseType, workflowsApi,
} from '../../../../services/workflowsApi';

interface WorkflowStepCardProps {
  /** Required for asset uploads (scoped to client). */
  clientId: string;
  step: Step;
  index: number;
  total: number;
  onChange: (next: Step) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const STEP_META: Record<Step['type'], { label: string; icon: React.ReactNode; tint: string; help: string }> = {
  ask: {
    label: 'Ask',
    icon: <MessageCircle size={14} />,
    tint: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    help: 'Send a question, wait for the recipient to reply.',
  },
  send_template: {
    label: 'Send message',
    icon: <Send size={14} />,
    tint: 'bg-blue-50 text-blue-700 border-blue-200',
    help: 'Send a message without waiting for a reply.',
  },
  delay: {
    label: 'Delay',
    icon: <Clock size={14} />,
    tint: 'bg-amber-50 text-amber-700 border-amber-200',
    help: 'Pause for a duration before continuing.',
  },
  branch: {
    label: 'Branch',
    icon: <GitBranch size={14} />,
    tint: 'bg-purple-50 text-purple-700 border-purple-200',
    help: 'Pick a path based on a previous response.',
  },
  finish: {
    label: 'Finish',
    icon: <Flag size={14} />,
    tint: 'bg-gray-100 text-gray-700 border-gray-200',
    help: 'End of workflow — runs any on-complete actions.',
  },
};

export const WorkflowStepCard: React.FC<WorkflowStepCardProps> = ({
  clientId,
  step,
  index,
  total,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}) => {
  const meta = STEP_META[step.type];
  const [open, setOpen] = useState(true);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100">
        <span className="text-gray-300 cursor-grab"><GripVertical size={14} /></span>
        <span className="text-xs text-gray-400 font-sans w-6">{String(index + 1).padStart(2, '0')}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${meta.tint}`}>
          {meta.icon} {meta.label}
        </span>
        <span className="text-xs text-gray-500 truncate flex-1">{step.id}</span>

        <div className="flex items-center gap-0.5 shrink-0">
          {onMoveUp && index > 0 && (
            <button onClick={onMoveUp} title="Move up" className="p-1 text-gray-400 hover:text-gray-700">
              <ChevronUp size={14} />
            </button>
          )}
          {onMoveDown && index < total - 1 && (
            <button onClick={onMoveDown} title="Move down" className="p-1 text-gray-400 hover:text-gray-700">
              <ChevronDown size={14} />
            </button>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-1 text-gray-400 hover:text-gray-700"
            title={open ? 'Collapse' : 'Expand'}
          >
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <button
            onClick={onRemove}
            title="Remove step"
            className="p-1 text-gray-300 hover:text-red-600"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      {open && (
        <div className="px-4 py-4 space-y-3">
          {step.type === 'ask' && (
            <AskBody clientId={clientId} step={step} onChange={onChange as any} />
          )}
          {step.type === 'send_template' && <SendBody step={step} onChange={onChange as any} />}
          {step.type === 'delay' && <DelayBody step={step} onChange={onChange as any} />}
          {step.type === 'branch' && <BranchBody step={step} onChange={onChange as any} />}
          {step.type === 'finish' && <FinishBody step={step} />}
          <p className="text-[11px] text-gray-400 leading-snug">{meta.help}</p>
        </div>
      )}
    </div>
  );
};

// -- Ask body ---------------------------------------------------------------

const AskBody: React.FC<{
  clientId: string;
  step: AskStep;
  onChange: (s: AskStep) => void;
}> = ({ clientId, step, onChange }) => {
  const set = (patch: Partial<AskStep>) => onChange({ ...step, ...patch });
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(
    Boolean(step.template_name || step.template_body_params?.length),
  );
  return (
    <>
      <div>
        <Label>Prompt sent to the recipient</Label>
        <textarea
          value={step.prompt || ''}
          onChange={(e) => set({ prompt: e.target.value })}
          rows={2}
          className={inputCls}
          placeholder="What would you like to ask?"
        />
      </div>

      {/* One image for the WHOLE question (e.g. a 1–5 scale card). Sent
          before the prompt. Different from per-choice images below — use
          this when a single graphic shows all the options at once. */}
      <QuestionImageEditor
        clientId={clientId}
        imageUrl={step.image_url}
        onChange={(url) => set({ image_url: url || undefined })}
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Response type</Label>
          <select
            value={step.response_type}
            onChange={(e) => set({ response_type: e.target.value as ResponseType })}
            className={inputCls}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="choice">Choice (one of)</option>
            <option value="emoji">Emoji choice</option>
            <option value="scale">Scale (1–10)</option>
          </select>
        </div>
        <div>
          <Label>Timeout (hours)</Label>
          <input
            type="number"
            min={1}
            value={step.timeout_hours ?? 6}
            onChange={(e) => set({ timeout_hours: Number(e.target.value) || 6 })}
            className={inputCls}
          />
        </div>
      </div>

      {(step.response_type === 'choice' || step.response_type === 'emoji') && (
        <ChoicesEditor clientId={clientId} step={step} onChange={onChange} />
      )}

      {step.response_type === 'scale' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Scale min</Label>
            <input
              type="number"
              value={step.scale_min ?? 1}
              onChange={(e) => set({ scale_min: Number(e.target.value) || 1 })}
              className={inputCls}
            />
          </div>
          <div>
            <Label>Scale max</Label>
            <input
              type="number"
              value={step.scale_max ?? 10}
              onChange={(e) => set({ scale_max: Number(e.target.value) || 10 })}
              className={inputCls}
            />
          </div>
        </div>
      )}

      {/* Save-as is universal (used in reports). Unit only matters for numeric. */}
      <div className={step.response_type === 'number' ? 'grid grid-cols-2 gap-3' : ''}>
        <div>
          <Label>
            Save reply as (optional)
            <span className="text-gray-400 font-normal"> — used in reports</span>
          </Label>
          <input
            value={step.store_as || ''}
            onChange={(e) => set({ store_as: e.target.value })}
            className={inputCls}
            placeholder="e.g. wellness_score"
          />
        </div>
        {step.response_type === 'number' && (
          <div>
            <Label>Unit (optional)</Label>
            <input
              value={step.unit || ''}
              onChange={(e) => set({ unit: e.target.value })}
              className={inputCls}
              placeholder="mg/dL"
            />
          </div>
        )}
      </div>

      {/* Advanced — Meta template fields, hidden by default. Most authors
          never need this; only the FIRST step of a scheduled workflow does. */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
        >
          <span>{showAdvanced ? '▾' : '▸'}</span>
          Advanced (Meta template, for proactive sends)
        </button>
        {showAdvanced && (
          <div className="mt-2">
            <TemplateFields
              templateName={step.template_name}
              templateLanguage={step.template_language}
              bodyParams={step.template_body_params}
              onChange={(patch) => set(patch as Partial<AskStep>)}
            />
          </div>
        )}
      </div>
    </>
  );
};

// -- Choices editor (per-row, with optional image upload) -------------------
//
// Renders one row per choice. Each row has the choice text + an inline
// image attachment control. When an image is attached, the URL is persisted
// into `step.choices_with_images[label]` and the engine will send each as
// its own WhatsApp image message before the question (so the recipient sees
// the visual reference). Renaming a choice migrates the image mapping to
// the new key so the linkage doesn't break.

// One image for the entire question — e.g. a "reply 1–5" scale card that
// shows every option in a single graphic. Sent as an image message before
// the prompt text. This is the simple path: upload one picture, the
// recipient sees it, then replies with a number/choice. Use the per-choice
// ChoicesEditor only when each option needs its OWN distinct image.
const QuestionImageEditor: React.FC<{
  clientId: string;
  imageUrl?: string;
  onChange: (url: string | null) => void;
}> = ({ clientId, imageUrl, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image file');
      return;
    }
    setUploading(true);
    try {
      const res = await workflowsApi.uploadAsset(clientId, file);
      onChange(res.url);
      toast.success('Question image attached');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>Question image (optional)</Label>
      {imageUrl ? (
        <div className="flex items-center gap-3">
          <img
            src={workflowsApi.previewUrl(imageUrl)}
            alt="Question"
            className="h-16 w-16 rounded-lg object-cover border border-gray-200"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-red-600 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="text-xs text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : '+ Attach an image for this question'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
      <p className="text-[11px] text-gray-400 mt-1 leading-snug">
        One image shown before the question — e.g. a card showing the 1–5 scale.
        The recipient sees it, then replies with a number. Under 5MB.
      </p>
    </div>
  );
};

const ChoicesEditor: React.FC<{
  clientId: string;
  step: AskStep;
  onChange: (s: AskStep) => void;
}> = ({ clientId, step, onChange }) => {
  const choices = step.choices || [];
  const images = step.choices_with_images || {};

  const setChoices = (next: string[], nextImages?: Record<string, string>) => {
    onChange({
      ...step,
      choices: next,
      choices_with_images: nextImages ?? images,
    });
  };

  const renameChoice = (i: number, value: string) => {
    const prev = choices[i];
    const next = [...choices];
    next[i] = value;
    // Migrate the image URL to the new key — and drop the old one — so the
    // mapping follows the label rename.
    const nextImages: Record<string, string> = { ...images };
    if (prev && prev !== value && nextImages[prev]) {
      const url = nextImages[prev];
      delete nextImages[prev];
      if (value) nextImages[value] = url;
    }
    setChoices(next, nextImages);
  };

  const addChoice = () => setChoices([...choices, '']);

  const removeChoice = (i: number) => {
    const label = choices[i];
    const next = choices.filter((_, idx) => idx !== i);
    const nextImages = { ...images };
    if (label) delete nextImages[label];
    setChoices(next, nextImages);
  };

  const setImageForChoice = (label: string, url: string | null) => {
    const next = { ...images };
    if (url) next[label] = url;
    else delete next[label];
    onChange({ ...step, choices_with_images: next });
  };

  return (
    <div>
      <Label>Choices (each appears as a tappable option)</Label>
      <div className="space-y-2">
        {choices.length === 0 && (
          <p className="text-xs text-gray-400 italic">
            No choices yet — add one below.
          </p>
        )}
        {choices.map((c, i) => (
          <ChoiceRow
            key={i}
            clientId={clientId}
            value={c}
            imageUrl={images[c]}
            placeholder={step.response_type === 'emoji' ? '😊 Happy' : 'yes'}
            onChange={(v) => renameChoice(i, v)}
            onRemove={() => removeChoice(i)}
            onImageChange={(url) => setImageForChoice(c, url)}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={addChoice}
        className="mt-2 text-xs text-emerald-700 hover:text-emerald-800 font-medium"
      >
        + Add choice
      </button>
      <p className="text-[11px] text-gray-400 mt-2 leading-snug">
        Attach an image to a choice (optional) to send a visual reference before the question.
        Images must be under 5MB. They&apos;re hosted on our servers and Meta downloads them when sending.
      </p>
    </div>
  );
};

const ChoiceRow: React.FC<{
  clientId: string;
  value: string;
  imageUrl?: string;
  placeholder: string;
  onChange: (v: string) => void;
  onRemove: () => void;
  onImageChange: (url: string | null) => void;
}> = ({ clientId, value, imageUrl, placeholder, onChange, onRemove, onImageChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const triggerUpload = () => {
    if (!value.trim()) {
      toast.error('Type the choice label first');
      return;
    }
    inputRef.current?.click();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same filename
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image file');
      return;
    }
    setUploading(true);
    try {
      const res = await workflowsApi.uploadAsset(clientId, file);
      onImageChange(res.url);
      toast.success('Image attached');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${inputCls} flex-1`}
      />

      {imageUrl ? (
        <div className="relative shrink-0">
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img
            src={workflowsApi.previewUrl(imageUrl)}
            alt={`Choice "${value}" image`}
            className="w-10 h-10 rounded-lg object-cover border border-gray-200"
          />
          <button
            type="button"
            onClick={() => onImageChange(null)}
            title="Remove image"
            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 flex items-center justify-center"
          >
            <XIcon size={10} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={triggerUpload}
          disabled={uploading}
          title="Attach image to this choice"
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 shrink-0 disabled:opacity-50 whitespace-nowrap"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
          {uploading ? 'Uploading…' : 'Image'}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
      />

      <button
        type="button"
        onClick={onRemove}
        title="Remove choice"
        className="p-1.5 text-gray-300 hover:text-red-600 shrink-0"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
};

// -- Send template body -----------------------------------------------------

const SendBody: React.FC<{ step: SendTemplateStep; onChange: (s: SendTemplateStep) => void }> = ({ step, onChange }) => {
  const set = (patch: Partial<SendTemplateStep>) => onChange({ ...step, ...patch });
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(
    Boolean(step.template_name || step.template_body_params?.length),
  );
  return (
    <>
      <div>
        <Label>Message body (plain text)</Label>
        <textarea
          value={step.body || ''}
          onChange={(e) => set({ body: e.target.value })}
          rows={3}
          className={inputCls}
          placeholder="Thanks for checking in today — talk to you tomorrow!"
        />
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
        >
          <span>{showAdvanced ? '▾' : '▸'}</span>
          Advanced (Meta template, for proactive sends)
        </button>
        {showAdvanced && (
          <div className="mt-2">
            <TemplateFields
              templateName={step.template_name}
              templateLanguage={step.template_language}
              bodyParams={step.template_body_params}
              onChange={(patch) => set(patch as Partial<SendTemplateStep>)}
            />
          </div>
        )}
      </div>
    </>
  );
};

// -- Template fields (shared between Ask + Send) -----------------------------

interface TemplateFieldsProps {
  templateName?: string;
  templateLanguage?: string;
  bodyParams?: string[];
  onChange: (patch: {
    template_name?: string;
    template_language?: string;
    template_body_params?: string[];
  }) => void;
}

const TemplateFields: React.FC<TemplateFieldsProps> = ({
  templateName,
  templateLanguage,
  bodyParams,
  onChange,
}) => {
  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-3">
      <div className="text-xs font-medium text-emerald-800 flex items-center gap-1.5">
        Meta-approved template
        <span className="text-emerald-700/70 font-normal">— required for proactive sends (outside 24h window)</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label>Template name</Label>
          <input
            value={templateName || ''}
            onChange={(e) => onChange({ template_name: e.target.value || undefined })}
            className={inputCls}
            placeholder="navcro_daily_checkin"
          />
        </div>
        <div>
          <Label>Language</Label>
          <input
            value={templateLanguage || ''}
            onChange={(e) => onChange({ template_language: e.target.value || undefined })}
            className={inputCls}
            placeholder="en_US"
          />
        </div>
      </div>
      <div>
        <Label>
          Body variables (one per line — format: <code className="bg-white px-1 rounded">name=value</code>)
        </Label>
        <textarea
          value={(bodyParams || []).join('\n')}
          onChange={(e) =>
            onChange({
              template_body_params: e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          rows={3}
          className={inputCls}
          placeholder={'first_name={{recipient.first_name}}\ncohort={{recipient.cohort}}'}
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Names match the <code className="bg-gray-100 px-1 rounded">{'{{name}}'}</code> placeholders in your approved Meta template body. Values can be literals or use <code className="bg-gray-100 px-1 rounded">{'{{recipient.first_name}}'}</code> / <code className="bg-gray-100 px-1 rounded">{'{{recipient.<field>}}'}</code> for personalization.
        </p>
      </div>
    </div>
  );
};

// -- Delay body -------------------------------------------------------------

const DelayBody: React.FC<{ step: DelayStep; onChange: (s: DelayStep) => void }> = ({ step, onChange }) => {
  const set = (patch: Partial<DelayStep>) => onChange({ ...step, ...patch });
  return (
    <div className="grid grid-cols-3 gap-3">
      <div>
        <Label>Minutes</Label>
        <input
          type="number"
          min={0}
          value={step.duration_minutes ?? 0}
          onChange={(e) => set({ duration_minutes: Number(e.target.value) || 0 })}
          className={inputCls}
        />
      </div>
      <div>
        <Label>Hours</Label>
        <input
          type="number"
          min={0}
          value={step.duration_hours ?? 0}
          onChange={(e) => set({ duration_hours: Number(e.target.value) || 0 })}
          className={inputCls}
        />
      </div>
      <div>
        <Label>Days</Label>
        <input
          type="number"
          min={0}
          value={step.duration_days ?? 0}
          onChange={(e) => set({ duration_days: Number(e.target.value) || 0 })}
          className={inputCls}
        />
      </div>
    </div>
  );
};

// -- Branch body ------------------------------------------------------------

const BranchBody: React.FC<{ step: BranchStep; onChange: (s: BranchStep) => void }> = ({ step, onChange }) => {
  const set = (patch: Partial<BranchStep>) => onChange({ ...step, ...patch });
  const conditions = step.conditions || [];

  const addCondition = () => set({ conditions: [...conditions, { if: '', goto: '' }] });
  const updateCondition = (i: number, patch: Partial<{ if: string; goto: string }>) =>
    set({ conditions: conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const removeCondition = (i: number) =>
    set({ conditions: conditions.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="space-y-2">
        {conditions.map((c, i) => (
          <div key={i} className="flex gap-2 items-start">
            <input
              value={c.if}
              onChange={(e) => updateCondition(i, { if: e.target.value })}
              placeholder="responses.q4 == 'no'"
              className={`${inputCls} flex-1 font-sans text-xs`}
            />
            <span className="text-xs text-gray-400 mt-2.5">→</span>
            <input
              value={c.goto}
              onChange={(e) => updateCondition(i, { goto: e.target.value })}
              placeholder="step_id"
              className={`${inputCls} w-32 font-sans text-xs`}
            />
            <button
              onClick={() => removeCondition(i)}
              className="p-2 text-gray-300 hover:text-red-600"
              type="button"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addCondition}
          className="text-xs text-emerald-700 hover:text-emerald-800 font-medium"
        >
          + Add condition
        </button>
      </div>
      <div>
        <Label>Default next step (if no condition matches)</Label>
        <input
          value={step.default_goto || ''}
          onChange={(e) => set({ default_goto: e.target.value })}
          className={`${inputCls} font-sans text-xs`}
          placeholder="step_id"
        />
      </div>
    </>
  );
};

// -- Finish body ------------------------------------------------------------

const FinishBody: React.FC<{ step: FinishStep }> = ({ step }) => {
  const actions = step.on_complete || [];
  if (actions.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        No on-complete actions. Recipient marked as completed; their session ends here.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <Label>On complete</Label>
      <ul className="text-xs text-gray-700 space-y-1">
        {actions.map((a, i) => (
          <li key={i} className="font-sans bg-gray-50 px-2 py-1 rounded">{JSON.stringify(a)}</li>
        ))}
      </ul>
    </div>
  );
};

// -- shared --------------------------------------------------------------------

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-medium text-gray-600 mb-1">{children}</label>
);

const inputCls =
  'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200';

export default WorkflowStepCard;
